import React, { useEffect } from 'react';
import { Alert, Box, CircularProgress, Grid, Card, CardContent, CardActions, Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CURRENCY_SIGNS, STATUS } from '~/literals';
import { currencySelector, selectItineraryPackages } from '~/selectors';
import { getItineraryPackages } from '~/store/slices/itinerary-packages';
import { setWizardStep, startWizard } from '~/store/slices/itinerary-wizard';
import { ItineraryPackage } from '~/types';
import { trackButtonClick } from '~/utils/analytics';
import { createItineraryIdempotencyKey } from '~/utils/itinerary-checkout';

export type PackageDef = ItineraryPackage;

type Props = {
  packages?: PackageDef[];
  onSelect?: (p: PackageDef) => void;
};

const ItineraryPackages: React.FC<Props> = ({ packages: packagesOverride, onSelect }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedCurrency = useSelector(currencySelector);
  const {
    packages: apiPackages,
    pageStatus,
    alertMessage,
  } = useSelector(selectItineraryPackages);
  const packages = packagesOverride || apiPackages;

  useEffect(() => {
    dispatch(getItineraryPackages());
  }, [dispatch]);

  const handleSelect = (p: PackageDef) => {
    trackButtonClick('ItineraryPackages', `Select package: ${p.title} (${p.id})`);

    if (onSelect) {
      onSelect(p);
      return;
    }

    const idempotencyKey = createItineraryIdempotencyKey();
    dispatch(startWizard({ idempotencyKey, packageId: p.id }));
    dispatch(setWizardStep(2));
    navigate(`/itinerary/${idempotencyKey}`);
  };

  if (!packagesOverride && pageStatus === STATUS.RUNNING) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!packagesOverride && pageStatus === STATUS.ERROR) {
    return <Alert severity="error">{alertMessage || 'Could not load itinerary packages.'}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {packages.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.id} sx={{ display: { sm: 'flex' } }}>
            <Card sx={{ display: { sm: 'flex' }, flexDirection: { sm: 'column' }, width: '100%' }}>
              <CardContent sx={{ flexGrow: { sm: 1 } }}>
                <Typography variant="h6">{p.title}</Typography>
                {typeof p.max_travelers === 'number' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Up to {p.max_travelers} passenger{p.max_travelers === 1 ? '' : 's'}
                  </Typography>
                )}
                {p.features && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                    {p.features.map((f, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckIcon color="success" sx={{ fontSize: 18, mt: 0.25 }} />
                        <Typography variant="body2" color="text.secondary">{f}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {p.price?.[selectedCurrency] && (
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {CURRENCY_SIGNS[selectedCurrency]}{p.price[selectedCurrency]} {selectedCurrency}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => handleSelect(p)}>Select</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ItineraryPackages;
